import { useState, useEffect } from "react";
import { Menu, X, Download, ChevronDown } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { HOME } from "@/constants/testIds";
import { cn } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const primaryLinks = [
  {
    to: "/features",
    label: "Features",
    testId: HOME?.navFeatures,
  },
  {
    to: "/shops/register",
    label: "For Businesses",
    testId: HOME?.navShops,
  },
  {
    to: "/ambassador",
    label: "Ambassador",
    testId: HOME?.navAmbassador,
  },
  {
    to: "/team",
    label: "Our Team",
  },
  {
    to: "/about-digiswing",
    label: "About Us",
  },
];

const moreLinks = [
  { to: "/about", label: "About Time2Cash" },
  { to: "/reviews", label: "Reviews" },
  { to: "/gallery", label: "Gallery" },
  { to: "/blog", label: "Blog & Updates" },
  { to: "/careers", label: "Careers" },
  { to: "/faq", label: "Help & FAQ" },
  { to: "/contact", label: "Contact Us" },
  { to: "/links", label: "Quick Links" },
];

const mobileLinks = [
  { to: "/", label: "Home" },
  ...primaryLinks,
  ...moreLinks,
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <header
      className={cn(
        "fixed top-0 inset-x-0 z-50 transition-all duration-300",
        scrolled || pathname !== "/"
          ? "backdrop-blur-xl bg-[#FDFBF7]/85 border-b border-[#EBE8E3]"
          : "bg-transparent"
      )}
    >
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">

        {/* Logo */}
        <Link
          to="/"
          data-testid={HOME?.navLogo}
          className="flex items-center gap-2"
        >
          <img
          src="/Media-Library/logo.png"
          alt="Time2Cash Logo"
          className="h-10 w-10 object-contain rounded-sm"
        />

          <span className="font-extrabold text-lg tracking-tight text-[#1A1918]">
            Time<span className="text-[#FF5A00]">2</span>Cash
          </span>
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center gap-1">

          <Link
            to="/"
            data-testid={HOME?.navHome}
            className="px-3 py-2 text-sm font-medium text-[#4A4846] hover:text-[#FF5A00] transition-colors"
          >
            Home
          </Link>

          {primaryLinks.map((link) => (
            <Link
              key={link.label}
              to={link.to}
              data-testid={link.testId || undefined}
              className="px-3 py-2 text-sm font-medium text-[#4A4846] hover:text-[#FF5A00] transition-colors"
            >
              {link.label}
            </Link>
          ))}

          <DropdownMenu>
            <DropdownMenuTrigger
              data-testid={HOME?.navMore}
              className="px-3 py-2 text-sm font-medium text-[#4A4846] hover:text-[#FF5A00] transition-colors inline-flex items-center gap-1 outline-none"
            >
              More
              <ChevronDown className="w-4 h-4" />
            </DropdownMenuTrigger>

            <DropdownMenuContent align="end" className="w-56">

              {moreLinks.map((link) => (
                <DropdownMenuItem key={link.label} asChild>
                  <Link
                    to={link.to}
                    className="cursor-pointer"
                  >
                    {link.label}
                  </Link>
                </DropdownMenuItem>
              ))}

            </DropdownMenuContent>
          </DropdownMenu>

        </nav>

        {/* Right Side */}
        <div className="flex items-center gap-2">

          <Link
            to="/#download"
            data-testid={HOME?.navDownloadCta}
            className="hidden sm:inline-flex items-center gap-2 rounded-full bg-[#FF5A00] hover:bg-[#E65100] text-white px-5 py-2.5 text-sm font-semibold shadow-lg shadow-orange-500/25 transition-colors"
          >
            <Download className="w-4 h-4" />
            Download Now
          </Link>

          <button
            data-testid={HOME?.navMobileToggle}
            onClick={() => setOpen(!open)}
            className="lg:hidden p-2 rounded-lg hover:bg-[#FFF3ED]"
            aria-label="Toggle menu"
          >
            {open ? (
              <X className="w-5 h-5" />
            ) : (
              <Menu className="w-5 h-5" />
            )}
          </button>

        </div>

      </div>

      {/* Mobile Menu */}

      {open && (
        <div
          data-testid={HOME?.navMobileMenu}
          className="lg:hidden border-t border-[#EBE8E3] bg-white max-h-[80vh] overflow-y-auto"
        >
          <div className="container mx-auto max-w-7xl px-4 py-3 flex flex-col">

            {mobileLinks.map((link) => (
              <Link
                key={link.label}
                to={link.to}
                className="py-2.5 text-sm font-medium text-[#4A4846] hover:text-[#FF5A00]"
              >
                {link.label}
              </Link>
            ))}

            <Link
              to="/#download"
              className="mt-3 rounded-full bg-[#FF5A00] text-white text-center py-3 text-sm font-semibold"
            >
              Download Now
            </Link>

          </div>
        </div>
      )}
    </header>
  );
}